// components/ui/card.tsx
import * as React from "react"
import { cn } from "lib/utils"

type CardVariant = "plain" | "total" | "positive" | "negative" | "neutral"

const VARIANT_CLASSES: Record<Exclude<CardVariant, "plain">, string> = {
  total:
    "border-blue-200 bg-[radial-gradient(120%_120%_at_0%_0%,theme(colors.blue.100/.7),theme(colors.blue.50/.5)_40%,transparent_70%)]",
  positive:
    "border-green-200 bg-[radial-gradient(120%_120%_at_0%_0%,theme(colors.green.100/.7),theme(colors.green.50/.5)_40%,transparent_70%)]",
  negative:
    "border-red-200 bg-[radial-gradient(120%_120%_at_0%_0%,theme(colors.rose.100/.7),theme(colors.rose.50/.5)_40%,transparent_70%)]",
  neutral:
    "border-slate-200 bg-[radial-gradient(120%_120%_at_0%_0%,theme(colors.slate.100/.65),theme(colors.slate.50/.45)_40%,transparent_70%)]",
}

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = "plain", ...props }, ref) => {
    const wantsGradient = variant !== "plain"
    const gradientClasses =
      variant !== "plain" ? VARIANT_CLASSES[variant as Exclude<CardVariant, "plain">] : ""

    return (
      <div
        data-slot="card"
        ref={ref}
        className={cn(
          "relative rounded-xl border shadow-sm transition-shadow bg-card text-card-foreground",
          "hover:shadow-md",
          wantsGradient && gradientClasses,
          // overlays only when gradient is on
          wantsGradient &&
            [
              "before:absolute before:inset-0 before:rounded-[inherit] before:pointer-events-none before:opacity-60",
              "before:bg-gradient-to-b before:from-white/10 before:to-transparent",
              "after:absolute after:inset-0 after:rounded-[inherit] after:pointer-events-none after:opacity-[0.25]",
              "after:[background-image:radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.05)_1px,transparent_1px)] after:[background-size:10px_10px]",
              "dark:before:from-white/5 dark:after:opacity-[0.15]",
            ],
          className
        )}
        {...props}
      />
    )
  }
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
  )
)
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn("text-2xl font-semibold leading-none tracking-tight", className)} {...props} />
  )
)
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  )
)
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
)
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
)
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
